import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { PERMISSIONS } from "../Components/Permission";
import LoadingIndicator from "../Components/LoadingIndicator";

const API_URL = process.env.REACT_APP_API_URL;

const EditRole = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [role, setRole] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    if (!id) {
      toast.error("Role ID is missing");
      navigate("/role");
      return;
    }
    fetchRoleById();
  }, [id]);

  const fetchRoleById = async () => {
    setPageLoading(true);
    try {
      const response = await axios.post(API_URL, {
        action: "getRoleById",
        id,
      });

      const data = response.data;

      if (!data) {
        toast.error("Role not found");
        navigate("/role");
        return;
      }

      setRole(data.role || "");
      setSelectedPermissions(data.permissions || []);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to load role");
      navigate("/role");
    } finally {
      setPageLoading(false);
    }
  };

  const togglePermission = (key) => {
    setSelectedPermissions((prev) =>
      prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key],
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!role.trim()) {
      toast.error("Please enter role name");
      return;
    }
    if (selectedPermissions.length === 0) {
      toast.error("Please select at least one permission");
      return;
    }

    const userId = localStorage.getItem("userid");
    if (!userId) {
      toast.error("User not logged in");
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(API_URL, {
        action: "updateRole",
        id,
        role: role.trim().toLowerCase(),
        permissions: selectedPermissions,
        updatedBy: userId,
      });

      if (response.data) {
        toast.success("Role updated successfully");
        navigate("/role");
      } else {
        toast.error(response.data?.message || "Update failed");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Error updating role");
    } finally {
      setIsLoading(false);
    }
  };

  if (pageLoading) {
    return (
      <div className="employee-loading">
        <LoadingIndicator size={120} />
      </div>
    );
  }

  return (
    <div className="content-wrapper">
      <section className="content-header">
        <div className="container-fluid d-flex justify-content-end">
          <button
            className="btn btn-secondary"
            onClick={() => navigate("/role")}
          >
            Back
          </button>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-xl-7 col-lg-8 col-md-9 offset-md-1">
              <div className="card shadow-lg">
                <div className="card-header">
                  <h3 className="card-title font-weight-bold">Edit Role</h3>
                </div>

                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    <div className="form-group">
                      <label>Role Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={role}
                        onChange={(e) => setRole(e.target.value)}
                        disabled={isLoading}
                      />
                    </div>

                    <div className="form-group mt-3">
                      <label>Permissions</label>

                      <div className="row">
                        {PERMISSIONS.map((perm) => (
                          <div className="col-md-6 mb-2" key={perm.key}>
                            <div className="form-check">
                              <input
                                id={perm.key}
                                type="checkbox"
                                className="form-check-input"
                                checked={selectedPermissions.includes(perm.key)}
                                onChange={() => togglePermission(perm.key)}
                                disabled={isLoading}
                              />
                              <label
                                htmlFor={perm.key}
                                className="form-check-label"
                              >
                                {perm.label}
                              </label>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="text-center mt-4">
                      <button
                        type="submit"
                        className="btn btn-primary px-4"
                        disabled={isLoading}
                      >
                        {isLoading ? <LoadingIndicator size={22} /> : "Update"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EditRole;
